import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Bell, BellRing, Radio, Plus, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/PostCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtCountdown(iso, now) {
  const diff = new Date(iso).getTime() - now;
  if (diff <= 0) return "starting now";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}h`;
  if (h > 0) return `${h}h ${pad(m)}m`;
  return `${pad(m)}:${pad(sec)}`;
}

function gcalUrl(p) {
  const start = new Date(p.scheduled_at);
  const end = new Date(start.getTime() + 3600000);
  const fmt = (dt) => dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `NEXT UP Listen Party — ${p.track?.title || "Party"}`,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: `Join ${p.host?.artist_name || "the artist"}'s live listen party on NEXT UP.${p.note ? " " + p.note : ""}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function ScheduleModal({ open, onClose, onScheduled }) {
  const { user } = useAuth();
  const [myTracks, setMyTracks] = useState([]);
  const [trackId, setTrackId] = useState("");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/tracks").then(({ data }) => setMyTracks(data.filter((t) => t.user_id === user.id))).catch(() => {});
  }, [open, user.id]);

  const submit = async () => {
    if (!trackId || !when) { toast.error("Pick a track and a time"); return; }
    setBusy(true);
    try {
      await api.post("/scheduled-parties", { track_id: trackId, scheduled_at: new Date(when).toISOString(), note });
      toast.success("Party scheduled! 🎉");
      onScheduled(); onClose(); setTrackId(""); setWhen(""); setNote("");
    } catch { toast.error("Could not schedule"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181824] border-white/10 text-white" data-testid="schedule-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="w-5 h-5 text-emerald-400" /> Schedule a Party</DialogTitle></DialogHeader>
        {myTracks.length === 0 ? (
          <p className="text-sm text-zinc-400">Upload a track first to schedule a listen party.</p>
        ) : (
          <div className="space-y-4">
            <select value={trackId} onChange={(e) => setTrackId(e.target.value)} data-testid="schedule-track"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50 text-sm">
              <option value="">Choose a track…</option>
              {myTracks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-when"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50 text-sm [color-scheme:dark]" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid="schedule-note"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-emerald-500/50 text-sm" />
            <button onClick={submit} disabled={busy} data-testid="schedule-submit"
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50">
              {busy ? "Scheduling…" : "Schedule Party"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UpcomingPartiesCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = () => api.get("/scheduled-parties").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, []);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);

  const rsvp = async (id) => {
    try { const { data } = await api.post(`/scheduled-parties/${id}/rsvp`); setItems((prev) => prev.map((x) => (x.id === id ? { ...x, did_rsvp: data.did_rsvp, rsvp_count: data.rsvp_count } : x))); }
    catch {}
  };

  const start = async (id) => {
    try { const { data } = await api.post(`/scheduled-parties/${id}/start`); navigate(`/party/${data.party_id}`); }
    catch { toast.error("Could not start"); }
  };

  const canSchedule = user?.is_subscribed;

  return (
    <div className="nu-card p-5" data-testid="upcoming-parties-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center gap-2 font-bold"><CalendarClock className="w-4 h-4 text-emerald-400" /> Upcoming Parties</h3>
        {canSchedule && (
          <button onClick={() => setModalOpen(true)} data-testid="open-schedule-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold transition-colors"><Plus className="w-3.5 h-3.5" /> Schedule</button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500">No parties scheduled yet.{canSchedule ? " Be the first to set one up!" : ""}</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => {
            const diff = new Date(p.scheduled_at).getTime() - now;
            const soon = diff > 0 && diff <= 15 * 60 * 1000;
            return (
            <div key={p.id} className={`flex items-center gap-3 p-3 rounded-xl bg-black/40 border transition-colors ${soon ? "border-emerald-500/60 glow-emerald animate-pulse" : "border-white/10"}`} data-testid="scheduled-item">
              {p.track?.cover_url ? <img src={mediaUrl(p.track.cover_url)} alt="" className="w-11 h-11 rounded-lg object-cover" />
                : <span className="w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Radio className="w-5 h-5 text-emerald-400" /></span>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold truncate flex items-center gap-2">{p.track?.title}{soon && <span className="text-[9px] font-mono-nu uppercase tracking-widest text-emerald-400 px-1.5 py-0.5 rounded bg-emerald-500/15">Soon</span>}</p>
                <p className="text-xs text-zinc-500 truncate">{p.host?.artist_name} · <span className={`font-mono-nu ${soon ? "text-emerald-400" : "text-zinc-400"}`} data-testid="party-countdown">{fmtCountdown(p.scheduled_at, now)}</span> · {p.rsvp_count} going</p>
              </div>
              {p.is_host ? (
                <button onClick={() => start(p.id)} data-testid="start-scheduled-btn"
                  className="px-4 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-xs shrink-0">Start now</button>
              ) : (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <button onClick={() => rsvp(p.id)} data-testid="rsvp-btn"
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${p.did_rsvp ? "bg-emerald-500 text-black" : "bg-white/10 text-white hover:bg-white/20"}`}>
                    {p.did_rsvp ? <><BellRing className="w-3.5 h-3.5" /> Going</> : <><Bell className="w-3.5 h-3.5" /> RSVP</>}
                  </button>
                  {p.did_rsvp && (
                    <a href={gcalUrl(p)} target="_blank" rel="noreferrer" data-testid="add-to-calendar"
                      className="text-[10px] font-mono-nu text-emerald-400 hover:underline flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Add to Calendar</a>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      <ScheduleModal open={modalOpen} onClose={() => setModalOpen(false)} onScheduled={load} />
    </div>
  );
}
